import { Button } from '@/components/button';
import { FormLabel } from '@/components/form/formlabel';
import { Input } from '@/components/form/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/form/select';
import { Modal } from '@/components/modal';
import { HSeparator } from '@/components/separator';
import { DataTable } from '@/components/table/datatable';
import { clearErrors } from '@/reducers/ots';
import { setEditMode, setShowDelete } from '@/reducers/vouchers';
import { AppDispatch } from '@/root/store';
import { columns } from '@/routes/org/event/slug/transaction';
import { defaultVoucher } from '@/routes/org/event/slug/voucher/root';
import { ShowEvent } from '@/services/events';
import {
  DeleteVoucher,
  GetTransactionsByVoucher,
  ShowVoucher,
  UpdateVoucher,
} from '@/services/vouchers';
import { NoSymbolIcon } from '@heroicons/react/24/outline';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/solid';
import moment from 'moment';
import 'primereact/resources/themes/lara-light-cyan/theme.css';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

export function OrgEventDetailVouchersDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const { slug, id } = useParams();
  const dispatch: AppDispatch = useDispatch();
  const { voucher, transactions, editMode, showDelete, errors, paginator } =
    useSelector((state: any) => state.vouchers);
  const { event } = useSelector((state: any) => state.event);
  const [formData, setFormData] = useState(defaultVoucher);
  const options = ['Nonaktif', 'Aktif'];
  const [value, setValue] = useState(options[0]);

  const setData = (value: string, element: string) => {
    setFormData({ ...formData, [element]: value });

    dispatch(clearErrors());
  };

  useEffect(() => {
    dispatch(ShowVoucher(slug, id));
    dispatch(ShowEvent(slug));
  }, [slug, id]);

  useEffect(() => {
    setFormData({ ...voucher, status: voucher.status?.toString() });
  }, [voucher]);

  const submit = () => {
    dispatch(UpdateVoucher(event.id, voucher.id, formData));
  };

  const deleteVoucher = (voucherId: number) => {
    dispatch(DeleteVoucher(event.id, voucherId));

    navigate('/event/' + slug + '/voucher');
  };

  const queryParams = new URLSearchParams(location.search);
  const searchQuery: string = queryParams.get('search') ?? '';

  useEffect(() => {
    dispatch(GetTransactionsByVoucher(slug, id, searchQuery));
  }, [slug, searchQuery]);

  return (
    <>
      <Modal
        show={showDelete}
        onRequestClose={() => dispatch(setShowDelete(false))}
        width='medium'
        title='Hapus Voucher'
        body={
          <>
            <p>Apakah kamu yakin ingin menghapus voucher ini?</p>
          </>
        }
        footer={
          <div className='flex justify-end gap-3'>
            <Button
              variant='neutral'
              onClick={() => dispatch(setShowDelete(false))}
              leftIcon={<NoSymbolIcon />}
            >
              Batal
            </Button>
            <Button
              variant={'danger'}
              onClick={() => {
                deleteVoucher(voucher.id);
              }}
              leftIcon={<TrashIcon />}
            >
              Ya, Hapus
            </Button>
          </div>
        }
      />

      <div className='flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center'>
        <h1 className='text-lg font-medium sm:flex-grow'>Detail Voucher</h1>
        <Button
          color='danger'
          look='outline'
          leftIcon={<TrashIcon />}
          onClick={() => {
            dispatch(setShowDelete(true));
          }}
        >
          Hapus voucher
        </Button>
        <Button
          leftIcon={<PencilSquareIcon />}
          onClick={() => {
            if (!editMode) {
              dispatch(setEditMode(true));
            } else {
              submit();
            }
          }}
        >
          {!editMode ? 'Ubah voucher' : 'Simpan'}
        </Button>
      </div>
      <div className='grid grid-cols-3 gap-5'>
        <FormLabel label='Nama voucher' errorText={errors.name}>
          <Input
            type='text'
            value={formData?.name}
            disabled={!editMode}
            onChange={(e) => setData(e.target.value, 'name')}
          />
        </FormLabel>
        <FormLabel label='Jumlah voucher' errorText={errors.quantity}>
          <Input
            type='number'
            value={formData?.quantity}
            disabled={!editMode}
            onChange={(e) => setData(e.target.value, 'quantity')}
            required
          />
        </FormLabel>
        <FormLabel label='Tipe voucher' errorText={errors.type}>
          <Select
            disabled={!editMode}
            value={formData?.visibility}
            onValueChange={(e) => {
              setData(e, 'visibility');
            }}
          >
            <SelectTrigger>
              <SelectValue defaultValue='public' />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value={'public'}>Public</SelectItem>
                <SelectItem value={'private'}>Private</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </FormLabel>
      </div>
      <div className='grid grid-cols-3 gap-5'>
        <FormLabel label='Voucher mulai valid' errorText={errors.valid_from}>
          <Input
            type='datetime-local'
            disabled={!editMode}
            value={moment(formData?.valid_from).format('YYYY-MM-DDTHH:mm')}
            onChange={(e) => setData(e.target.value, 'valid_from')}
          />
        </FormLabel>
        <FormLabel label='Voucher berakhir' errorText={errors.valid_until}>
          <Input
            type='datetime-local'
            disabled={!editMode}
            value={moment(formData?.valid_until).format('YYYY-MM-DDTHH:mm')}
            onChange={(e) => setData(e.target.value, 'valid_until')}
          />
        </FormLabel>
        <FormLabel label='Voucher Status' errorText={errors.status}>
          <Select
            disabled={!editMode}
            value={formData.status?.toString()}
            onValueChange={(e: any) => setData(e, 'status')}
          >
            <SelectTrigger>
              <SelectValue defaultValue='1' />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value={'1'}>Aktif</SelectItem>
                <SelectItem value={'0'}>Nonaktif</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </FormLabel>
      </div>
      <HSeparator />
      <div>
        <DataTable
          name='Riwayat Transaksi'
          noun='transaksi'
          columns={columns}
          data={transactions}
          total={paginator.to}
          currentTotal={paginator.total}
          currentPage={paginator.current_page}
          pages={paginator.last_page}
        />
      </div>
    </>
  );
}
